/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent as F, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { validateLoginAsync } from "../../core/store/user/user.thunks";
import { Creds, forgotPassword } from "../../core/http/user";
import { useAppDispatch } from "../../core/store/hooks";
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Container from "../../components/UI/Container/Container";
import { ACCOUNT, NEW, PROTOCOL, SAVE_MM_PROTOCOL, SAVE_PUBLIC_PROTOCOL } from "../../core/router/paths";
import Button from "../../components/UI/Button/Button";
import PageHeadline from "../../components/UI/Text/PageHeadline";
import { validateEmail, validatePassword, validateUsername } from "../../utils/validate";
import { useDispatch, useSelector } from "react-redux";
import { IState } from "../../core/store/store";
import { duplicateMmProtocolAsync, shareProtocolAsync } from "../../core/store/protocol/protocol.thunks";
import { updateSaveMmProtocolId, updateSavePublicProtocolId } from "../../core/store/protocol/protocol.slice";


interface LocationState
{
  redirectTo?: string;
}

const Login: F = () =>
{
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [locationState] = useState<LocationState | undefined>(location.state);

  const user = useSelector((state: IState) => state.user);
  const savePublicProtocolId = useSelector((state: IState) => state.protocol.savePublicProtocolId);
  const saveMmProtocolId = useSelector((state: IState) => state.protocol.saveMmProtocolId);

  let passwordInputElement = useRef<HTMLInputElement | null>();
  let usernameInputElement = useRef<HTMLInputElement | null>();
  let messageElement = useRef<HTMLElement | null>();

  useEffect(() =>
  {
    if(user.username)
      return navigate(ACCOUNT);

    usernameInputElement.current = document.getElementById('username') as HTMLInputElement,
    passwordInputElement.current = document.getElementById('password') as HTMLInputElement;
    messageElement.current = document.getElementById("message") as HTMLElement;
  }, [user]);

  const [message, setMessage] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
  {
    setUsername(event.target.value);
    setUsernameError(!validateUsername(event.target.value) && !validateEmail(event.target.value));
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) =>
  {
    setPassword(event.target.value);
    setPasswordError(!validatePassword(event.target.value));
  };

  const handleKeyPress = (event: any) =>
  {
    if (messageElement.current && event.key === 'Enter')
    {
      messageElement.current.innerText = "";
      handleSubmit(username, password);
    }
    return false;
  };

  const focusAndSelect = (elem: HTMLInputElement) =>
  {
    elem.focus();
    elem.select();
  };

  const giveConditionalFocus = (message: string) =>
  {
    if(message === 'Invalid username.')
    {
      if(usernameInputElement.current)
        focusAndSelect(usernameInputElement.current);
    }
    else if(message === 'Invalid password.')
    {
      if(passwordInputElement.current)
        focusAndSelect(passwordInputElement.current);
    }
  };

  async function handleSubmit(username: string, password: string)
  {
    if(usernameError || passwordError || !username || !password)
      return;

    const login: Creds = { username, password };
    const savePublicProtocol = location.pathname.split("login/")[1]?.toLowerCase() === SAVE_PUBLIC_PROTOCOL;
    const saveMmProtocol = location.pathname.split("login/")[1]?.toLowerCase() === SAVE_MM_PROTOCOL;

    appDispatch(validateLoginAsync(login, !savePublicProtocol))
      .then(async data =>
      {
        if(!data)
          return setMessage("Login unsuccessful. Refresh and try again.");

        if(messageElement.current)
          messageElement.current.innerText = message;

        giveConditionalFocus(data.message + ".");
        if(data.success)
        {
          if(savePublicProtocol && savePublicProtocolId && data.userId)
          {
            await appDispatch(shareProtocolAsync(savePublicProtocolId, data.userId));
            dispatch(updateSavePublicProtocolId(undefined));
            return navigate(locationState?.redirectTo || (PROTOCOL + "/" + savePublicProtocolId));
          }
          else if(saveMmProtocol && saveMmProtocolId && data.userId)
          {
            await appDispatch(duplicateMmProtocolAsync(saveMmProtocolId));
            dispatch(updateSaveMmProtocolId(undefined));
            return navigate(locationState?.redirectTo || (PROTOCOL + "/" + saveMmProtocolId));
          }

          navigate(locationState?.redirectTo || PROTOCOL);
        }
        else
        {
          setMessage(data.message + ".");
        }
      })
      .catch(() => setMessage("Refresh and try again :)"));
  }

  function handleForgotPassword()
  {
    if(!username)
      setMessage("Enter your username or email.");

    forgotPassword(username);
    setMessage("Sending a password reset link to your email.");
  }



  return(
    <Container>
      <Stack sx={{ pt: 10 }}
        direction='column' justifyContent='center' alignItems='center' spacing={2}>
        <PageHeadline>Log in</PageHeadline>

        <Typography
          id='message'
          sx={{ fontStyle: 'italic', height: 20, pb: '20px'}}
          variant='subtitle1'
          gutterBottom>
          {message}
        </Typography>

        <TextField
          label='Username/Email'
          id='username'
          variant='outlined'
          size='small'
          sx={{ width: '200px' }}
          error={usernameError}
          onChange={handleUsernameChange}
          onKeyPress={handleKeyPress}
          autoFocus />

        <TextField
          label='Password'
          id='password'
          variant='outlined'
          type='password'
          size='small'
          sx={{ width: '200px' }}
          error={passwordError}
          onChange={handlePasswordChange}
          onKeyPress={handleKeyPress} />

        <Button
          id='LogIn'
          onClick={() => handleSubmit(username, password)}
          label='Log in'/>

        <Button
          id='ForgotPassword'
          onClick={handleForgotPassword}
          label='Forgot password'/>

        <Button
          onClick={() => navigate(NEW)}
          label='Create account 👤'/>
      </Stack>
    </Container>);
};

export default Login;
